import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import DynamicSidebar, {
  DRAWER_WIDTH_COLLAPSED,
  DRAWER_WIDTH_EXPANDED,
} from '../DynamicSidebar';
import { DashboardSectionSkeleton } from '../common/loading';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPages } from '../../hooks/useUserPages';
import { SEARCE_COLORS } from '../../theme';
import { hasAdminAccess } from '../../utils/AdminLogic';
import { isCustomerSentimentPage, getActiveTabFromRoute } from '../../utils/PageUtils';
import { trackPageView } from '../../utils/analytics';
import PageMarquee from '../common/PageMarquee';
import GlobalMarquee from '../common/GlobalMarquee';
import { pathnameToPageSlug } from '../../utils/PageUtils';

const SIDEBAR_EXPANDED_STORAGE_KEY = 'dashboard-sidebar-expanded';

const readSidebarExpandedPreference = (): boolean => {
  try {
    const v = localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY);
    if (v === 'true') return true;
    if (v === 'false') return false;
  } catch {
    /* ignore */
  }
  return false;
};

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarExpandedPreference);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { pages, loading: pagesLoading } = useUserPages();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check if user has admin access (admin/super_admin roles only)
  const userHasAdminAccess = hasAdminAccess(user);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Set active tab based on current route for header information and sidebar highlight
  useEffect(() => {
    const activeTabName = getActiveTabFromRoute({
      pathname: location.pathname,
      pages,
      authLoading,
      user,
      userHasAdminAccess,
      navigate,
    });
    setActiveTab(activeTabName);
  }, [location.pathname, user, userHasAdminAccess, navigate, pages, authLoading]);

  useEffect(() => {
    setDescriptionExpanded(false);
  }, [activeTab]);

  useEffect(() => {
    const width = isMobile ? 0 : sidebarOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED;
    document.documentElement.style.setProperty('--dashboard-sidebar-width', `${width}px`);
    return () => {
      document.documentElement.style.removeProperty('--dashboard-sidebar-width');
    };
  }, [sidebarOpen, isMobile]);

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const handleTabChange = (tab: string) => {
    // Check admin access before allowing tab change
    if (tab === 'admin') {
      if (!userHasAdminAccess) {
        return;
      }
    }

    // Normalize tab name for matching (handle case variations, spaces, underscores, etc.)
    const normalizedTab = tab.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');

    // Navigation logic
    if (normalizedTab === 'important-links') {
      navigate('/my-files');
    } else if (normalizedTab === 'dashboard') {
      navigate('/dashboard');
    } else if (normalizedTab === 'projects-portfolio') {
      navigate('/projects/portfolio');
    } else if (normalizedTab === 'ai-chatbot') {
      navigate('/ai-chatbot');
    } else if (normalizedTab === 'projects') {
      navigate('/projects');
    } else if (normalizedTab === 'people-analytics') {
      navigate('/people-analytics');
    } else if (
      normalizedTab === 'hr-stats' ||
      normalizedTab === 'people-insights' ||
      (normalizedTab.includes('hr') && normalizedTab.includes('stat'))
    ) {
      navigate('/hr-stats');
    } else if (normalizedTab === 'squad-structure') {
      navigate('/squad-structure');
    } else if (normalizedTab === 'requests') {
      navigate('/internal-project-request');
    } else if (normalizedTab === 'my-insights') {
      navigate('/my-insights');
    } else if (normalizedTab === 'my-team') {
      navigate('/my-team');
    } else if (normalizedTab === 'user-feedback') {
      navigate('/user-feedback');
    } else if (normalizedTab === 'customer-sentiment' ||
               normalizedTab === 'customer-feedback-analytics' ||
               normalizedTab.startsWith('customer-feedback') ||
               normalizedTab.startsWith('customer-sentiment')) {
      navigate('/customer-sentiment');
    } else if (normalizedTab === 'my-files') {
      navigate('/my-files');
    } else if (normalizedTab === 'dri-insights' || 
               (normalizedTab.includes('dri') && normalizedTab.includes('insight'))) {
      navigate('/dri-insights');
    } else if (normalizedTab === 'forms') {
      navigate('/forms');
    } else if (normalizedTab === 'admin') {
      navigate('/admin');
    } else if (normalizedTab === 'searce-assets') {
      navigate('/searce-assets');
    } else if (normalizedTab === 'sandbox-billing') {
      navigate('/sandbox-billing');
    } else if (normalizedTab === 'solution-story' || normalizedTab === 'solution-stories') {
      navigate('/solution-story');
    } else {
      if (normalizedTab.includes('customer') && (normalizedTab.includes('feedback') || normalizedTab.includes('sentiment'))) {
        navigate('/customer-sentiment');
      } else {
        navigate(`/${normalizedTab}`);
      }
    }
  };

  // Derive page info for header
  const currentPage = pages.find(page => page.name === activeTab);
  const pageName = currentPage?.name || activeTab;
  const isCustomerSentiment = isCustomerSentimentPage(pageName);
  
  // Hide header for specific pages that don't follow dashboard look
  const hideHeader = isCustomerSentiment || 
                     location.pathname === '/squad-structure' || 
                     location.pathname === '/forms' ||
                     location.pathname === '/customer-feedback' ||
                     location.pathname === '/searce-assets' ||
                     location.pathname === '/ai-chatbot' ||
                     // People Insights renders its own sticky header
                     // (PeopleInsightsHeader) with breadcrumb, title, and
                     // filter row, so the layout-level header would just
                     // be a duplicate.
                     location.pathname === '/hr-stats' ||
                     location.pathname === '/sandbox-billing' ||
                     location.pathname === '/solution-story' ||
                     location.pathname === '/solution-stories/admin';

  const showWorkspaceSkeleton = pagesLoading && pages.length === 0;

  /** Pages with FilterStripLayout render marquee above filters; others use layout-level banner. */
  const FILTER_STRIP_PAGE_SLUGS = new Set([
    'dashboard',
    'my-insights',
    'my-team',
    'projects',
    'dri-insights',
  ]);
  const showLayoutMarquee = !FILTER_STRIP_PAGE_SLUGS.has(pathnameToPageSlug(location.pathname));

  const pageDescription = currentPage?.description?.trim() ?? '';
  const hasPageDescription = pageDescription.length > 0;

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Persistent Sidebar (permanent on desktop, pull-out overlay on mobile) */}
      <DynamicSidebar
        open={sidebarOpen}
        onToggle={handleSidebarToggle}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isMobile={isMobile}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      {/* Mobile-only button to pull out the sidebar */}
      {isMobile && !mobileNavOpen && (
        <IconButton
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open navigation"
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 1301,
            backgroundColor: SEARCE_COLORS.primary,
            color: SEARCE_COLORS.white,
            boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
            '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.88) },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Main Content Area */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: SEARCE_COLORS.background,
        overflow: 'hidden', // Contain scrolling to inner boxes
        height: '100vh',
      }}>
        {/* Modern Header */}
        {!hideHeader && (
          <Box sx={{
            backgroundColor: SEARCE_COLORS.white,
            borderBottom: `1px solid ${SEARCE_COLORS.neutral_200}`,
            px: 4,
            py: 1,
            zIndex: 1200,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  width: '100%',
                  mb: hasPageDescription ? 0 : 0.5,
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 700,
                    fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' },
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(135deg, ${SEARCE_COLORS.primary} 0%, ${SEARCE_COLORS.secondary} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {currentPage?.display_name ||
                      (activeTab === 'requests'
                        ? 'Internal Project Request'
                        : activeTab === 'dashboard'
                          ? 'Resources Utilization'
                          : activeTab === 'important-links'
                            ? 'Important Links'
                            : activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace(/-/g, ' '))}
                  </Box>
                </Typography>
                {hasPageDescription && (
                  <IconButton
                    size="small"
                    onClick={() => setDescriptionExpanded((open) => !open)}
                    aria-expanded={descriptionExpanded}
                    aria-label={descriptionExpanded ? 'Hide page description' : 'Show page description'}
                    sx={{
                      flexShrink: 0,
                      width: 24,
                      height: 24,
                      padding: 0,
                      borderRadius: '50%',
                      backgroundColor: SEARCE_COLORS.primary,
                      color: SEARCE_COLORS.white,
                      boxShadow: `0 1px 4px ${alpha(SEARCE_COLORS.primary, 0.35)}`,
                      '&:hover': {
                        backgroundColor: alpha(SEARCE_COLORS.primary, 0.88),
                        color: SEARCE_COLORS.white,
                      },
                    }}
                  >
                    <ExpandMoreIcon
                      sx={{
                        color: SEARCE_COLORS.white,
                        fontSize: 22,
                        transition: 'transform 0.2s ease',
                        transform: descriptionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </IconButton>
                )}
              </Box>
              {hasPageDescription && (
                <Collapse in={descriptionExpanded}>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      color: '#252524',
                      fontWeight: 350,
                      fontSize: '0.875rem',
                      pt: 0.5,
                      pb: 0.5,
                    }}
                  >
                    {pageDescription}
                  </Typography>
                </Collapse>
              )}
            </Box>
          </Box>
        )}

        {/* Scrollable workspace: parent column uses overflow:hidden + flex; this region must flex + minHeight:0 or content is clipped with no scroll */}
        <Box
          id="dashboard-workspace-scroll"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {showWorkspaceSkeleton ? (
            <Box role="status" aria-live="polite" aria-busy>
              <DashboardSectionSkeleton />
            </Box>
          ) : (
            <>
              <GlobalMarquee />
              {showLayoutMarquee && <PageMarquee />}
              <Outlet />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
 